import { supabase } from '../lib/supabase';

export const stripeService = {
  // Validate coupon code
  async validateCouponCode(couponCode) {
    try {
      const { data, error } = await supabase
        ?.from('coupon_codes')
        ?.select('*')
        ?.eq('code', couponCode?.toUpperCase())
        ?.eq('is_active', true)
        ?.single();

      if (error) {
        return { valid: false, error: 'Invalid coupon code' };
      }

      // Check if expired
      if (data?.valid_until && new Date(data?.valid_until) < new Date()) {
        return { valid: false, error: 'Coupon code has expired' };
      }

      // Check redemption limit
      if (data?.max_redemptions && data?.current_redemptions >= data?.max_redemptions) {
        return { valid: false, error: 'Coupon code has reached maximum uses' };
      }

      return { valid: true, coupon: data, error: null };
    } catch (error) {
      console.error('Validate coupon error:', error);
      return { valid: false, error: error?.message || 'Failed to validate coupon' };
    }
  },

  // Activate Pilot Mode (PILOT2MONTHS coupon) - grants immediate Pro access without Stripe
  async activatePilotMode(userId, couponId) {
    try {
      console.log('Activating Pilot Mode:', { userId, couponId });
      
      // Calculate expiration date (60 days from now)
      const expirationDate = new Date();
      expirationDate?.setDate(expirationDate?.getDate() + 60);
      
      // Update user profile to Pro with pilot mode
      const { data: profileData, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.update({
          subscription_tier: 'pro',
          subscription_status: 'trialing',
          subscription_source: 'pilot_coupon',
          subscription_end_date: expirationDate?.toISOString(),
          pilot_warning_shown: false,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userId)
        ?.select()
        ?.single();
      
      if (profileError) {
        console.error('Profile update error:', profileError);
        throw new Error(profileError?.message || 'Failed to activate Pilot Mode');
      }
      
      // Record coupon redemption
      const { error: redemptionError } = await supabase
        ?.from('coupon_redemptions')
        ?.insert({
          coupon_id: couponId,
          user_id: userId,
          stripe_subscription_id: null // No Stripe subscription for pilot mode
        });
      
      if (redemptionError) {
        console.error('Redemption tracking error:', redemptionError);
        // Don't fail the activation if redemption tracking fails
      }
      
      // Increment coupon redemption count
      const { error: incrementError } = await supabase
        ?.rpc('increment_coupon_redemptions', { coupon_id: couponId });
      
      if (incrementError) {
        console.error('Increment redemption error:', incrementError);
        // Don't fail the activation if increment fails
      }
      
      console.log('Pilot Mode activated successfully:', profileData);
      return { data: profileData, error: null };
      
    } catch (error) {
      console.error('Activate Pilot Mode error:', error);
      return { data: null, error: error?.message || 'Failed to activate Pilot Mode' };
    }
  },

  // Create checkout session for subscription
  async createCheckoutSession(planId, userId, userEmail, couponCode = null) {
    try {
      console.log('=== STRIPE CHECKOUT TROUBLESHOOTING START ===');
      console.log('1. Request Parameters:', { planId, userId, userEmail, couponCode });
      
      // Step 1: Get current session (don't force refresh yet)
      console.log('2. Checking current session validity...');
      const { data: { session: currentSession }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError) {
        console.error('❌ Session retrieval error:', sessionError);
        throw new Error('Authentication error. Please log out and log back in.');
      }
      
      if (!currentSession?.access_token) {
        console.error('❌ No valid session found');
        console.error('  - This usually means the user is not logged in');
        console.error('  - Or the session has expired and needs refresh');
        throw new Error('You must be logged in to upgrade your subscription. Please log in and try again.');
      }

      console.log('✅ Current session found');
      console.log('  - User ID:', currentSession?.user?.id);
      console.log('  - User Email:', currentSession?.user?.email);
      console.log('  - Access Token (first 30 chars):', currentSession?.access_token?.substring(0, 30));
      
      // Step 2: Verify token is not expired and is the correct type
      const tokenStart = currentSession?.access_token?.substring(0, 30);
      const isUserToken = tokenStart?.includes('eyJhbGciOiJFUzI1Ni'); // ES256 = user token
      const isAnonKey = tokenStart?.includes('eyJhbGciOiJIUzI1Ni'); // HS256 = anon key
      
      console.log('  - Token Algorithm Detection:');
      console.log('    * ES256 (User Token):', isUserToken ? '✅ CORRECT' : '❌ NOT DETECTED');
      console.log('    * HS256 (Anon Key):', isAnonKey ? '❌ WRONG TOKEN!' : '✅ Not anon key');
      
      if (isAnonKey) {
        console.error('❌ CRITICAL: Session contains anon key instead of user token!');
        console.error('  - This indicates a Supabase session corruption');
        console.error('  - Forcing sign out to clear corrupted session');
        await supabase?.auth?.signOut();
        throw new Error('Session corrupted. You have been logged out. Please log in again.');
      }
      
      // Step 3: Verify token expiration
      try {
        const tokenParts = currentSession?.access_token?.split('.');
        if (tokenParts?.length === 3) {
          const payloadB64 = tokenParts?.[1];
          const payloadJson = atob(payloadB64?.replace(/-/g, '+')?.replace(/_/g, '/'));
          const payload = JSON.parse(payloadJson);
          
          const now = Math.floor(Date.now() / 1000);
          const expiresAt = payload?.exp;
          const timeUntilExpiry = expiresAt - now;
          
          console.log('  - Token Expiration Check:');
          console.log('    * Expires at:', new Date(expiresAt * 1000)?.toISOString());
          console.log('    * Time until expiry:', timeUntilExpiry, 'seconds (', Math.floor(timeUntilExpiry / 60), 'minutes )');
          console.log('    * Is expired:', timeUntilExpiry <= 0);
          
          // Check if token is already expired
          if (timeUntilExpiry <= 0) {
            console.error('❌ Token is already expired');
            console.log('⚠️ Attempting to refresh expired token...');
            const { data: { session: refreshedSession }, error: refreshError } = await supabase?.auth?.refreshSession();
            
            if (refreshError || !refreshedSession?.access_token) {
              console.error('❌ Token refresh failed:', refreshError);
              await supabase?.auth?.signOut();
              throw new Error('Your session has expired. Please log in again to continue.');
            }
            
            console.log('✅ Expired token refreshed successfully');
            const finalToken = refreshedSession?.access_token;
            return await this._executeCheckout(planId, userId, userEmail, couponCode, finalToken);
          }
          
          // If token expires in less than 5 minutes, refresh it
          if (timeUntilExpiry < 300) {
            console.log('⚠️ Token expires soon, refreshing...');
            const { data: { session: refreshedSession }, error: refreshError } = await supabase?.auth?.refreshSession();
            
            if (refreshError || !refreshedSession?.access_token) {
              console.error('❌ Token refresh failed:', refreshError);
              await supabase?.auth?.signOut();
              throw new Error('Session expired. You have been logged out. Please log in again.');
            }
            
            console.log('✅ Token refreshed successfully');
            // Use the refreshed token
            const finalToken = refreshedSession?.access_token;
            return await this._executeCheckout(planId, userId, userEmail, couponCode, finalToken);
          }
          
          // Token is valid and not expiring soon, use it
          console.log('✅ Token is valid and not expiring soon');
          const finalToken = currentSession?.access_token;
          return await this._executeCheckout(planId, userId, userEmail, couponCode, finalToken);
        }
      } catch (decodeError) {
        console.error('⚠️ Could not decode token for expiration check:', decodeError);
        // If we can't decode, try to use the token anyway
        const finalToken = currentSession?.access_token;
        return await this._executeCheckout(planId, userId, userEmail, couponCode, finalToken);
      }
    } catch (error) {
      console.error('❌ Create checkout session error:', error);
      console.error('=== STRIPE CHECKOUT TROUBLESHOOTING END (CATCH) ===');
      return { data: null, error: error?.message || 'Failed to create checkout session' };
    }
  },

  // Helper method to execute the actual checkout call
  async _executeCheckout(planId, userId, userEmail, couponCode, accessToken) {
    // CRITICAL GUARD: Prevent Stripe price IDs from being used as planId
    if (planId?.startsWith('price_')) {
      console.error('❌ CRITICAL ERROR: Stripe price ID detected as planId!');
      console.error('  - Received planId:', planId);
      console.error('  - This should be a UUID from subscription_plans.id');
      console.error('  - NOT a Stripe price ID (price_...)');
      console.error('  - Check frontend plan selection logic');
      throw new Error('Invalid plan identifier. Please refresh the page and try again.');
    }

    // Validate planId is a UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex?.test(planId)) {
      console.error('❌ CRITICAL ERROR: planId is not a valid UUID!');
      console.error('  - Received planId:', planId);
      console.error('  - Expected format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx');
      console.error('  - Check frontend plan selection logic');
      throw new Error('Invalid plan identifier format. Please refresh the page and try again.');
    }

    console.log('✅ planId validation passed:', planId);

    const body = { 
      planId, 
      userId, 
      userEmail,
      successUrl: `${window.location?.origin}/billing-dashboard?success=true`,
      cancelUrl: `${window.location?.origin}/billing-dashboard?canceled=true`
    };

    // Add coupon code if provided
    if (couponCode) {
      body.couponCode = couponCode;
    }

    console.log('3. Edge Function Request:', {
      functionName: 'create-subscription-checkout',
      body,
      tokenPreview: accessToken?.substring(0, 30) + '...'
    });

    console.log('4. Invoking Edge Function with explicit Authorization header...');
    const startTime = Date.now();
    
    const { data, error } = await supabase?.functions?.invoke('create-subscription-checkout', {
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }
    });
    
    const duration = Date.now() - startTime;
    console.log(`5. Service call completed in ${duration} ms`);

    console.log('\n=== CHECKOUT RESPONSE ANALYSIS ===');
    console.log('6. Response received:', {
      hasData: !!data,
      hasError: !!error,
      dataType: typeof data,
      errorType: typeof error
    });

    // Handle different error types from Supabase Edge Functions
    if (error) {
      console.error('❌ Edge Function Error Details:');
      console.error('  - Error Object:', JSON.stringify(error, null, 2));
      console.error('  - Error Name:', error?.name);
      console.error('  - Error Message:', error?.message);
      console.error('  - Error Context:', error?.context);
      
      // NEW: Force-read response body to see actual gateway error message
      if (error?.context instanceof Response) {
        try {
          const responseClone = error?.context?.clone();
          const responseText = await responseClone?.text();
          console.error('  - Gateway Response Body:', responseText);
          
          // Try to parse as JSON
          try {
            const responseJson = JSON.parse(responseText);
            console.error('  - Parsed Gateway Error:', responseJson);
          } catch (jsonError) {
            console.error('  - Response is not JSON, raw text:', responseText);
          }
        } catch (readError) {
          console.error('  - Failed to read response body:', readError);
        }
      }
      
      // Extract the actual error message from various possible locations
      let errorMessage = 'Failed to create checkout session';
      
      // Try multiple paths to find the error message
      if (error?.context?.body) {
        try {
          const errorBody = typeof error?.context?.body === 'string' 
            ? JSON.parse(error?.context?.body) 
            : error?.context?.body;
          console.log('  - Parsed Error Body:', errorBody);
          errorMessage = errorBody?.error || errorBody?.details?.message || errorMessage;
        } catch (parseError) {
          console.error('  - Error parsing function response:', parseError);
        }
      } else if (error?.message) {
        errorMessage = error?.message;
      }
      
      // Special handling for 401 errors
      if (error?.status === 401 || error?.message?.includes('401')) {
        console.error('🔐 AUTHENTICATION ERROR DETECTED');
        console.error('  - This means the Edge Function gateway rejected the authentication');
        console.error('  - Check the Gateway Response Body above for the exact reason');
        errorMessage = 'Authentication failed. Your session may have been revoked. Please log out completely, clear your browser cache, and log back in.';
      }
      
      console.error('  - Final Error Message:', errorMessage);
      console.error('=== STRIPE CHECKOUT TROUBLESHOOTING END (ERROR) ===');
      throw new Error(errorMessage);
    }

    // Also check if data contains an error field (for 200 responses with error)
    if (data?.error) {
      console.error('❌ Data contains error:', data?.error);
      console.error('=== STRIPE CHECKOUT TROUBLESHOOTING END (DATA ERROR) ===');
      throw new Error(data.error);
    }

    // ✅ COMPREHENSIVE CHECKOUT URL VALIDATION
    console.log('\n7. Checkout URL Validation:');
    console.log('  - data.url exists:', !!data?.url);
    console.log('  - data.url type:', typeof data?.url);
    console.log('  - data.url value:', data?.url || 'MISSING');
    console.log('  - data.url length:', data?.url?.length || 0);
    console.log('  - data.url starts with https:', data?.url?.startsWith('https://'));
    console.log('  - data.url contains checkout.stripe.com:', data?.url?.includes('checkout.stripe.com'));
    
    console.log('\n8. Session ID Validation:');
    console.log('  - data.sessionId exists:', !!data?.sessionId);
    console.log('  - data.sessionId type:', typeof data?.sessionId);
    console.log('  - data.sessionId value:', data?.sessionId || 'MISSING');
    console.log('  - data.sessionId length:', data?.sessionId?.length || 0);
    console.log('  - data.sessionId starts with cs_:', data?.sessionId?.startsWith('cs_'));
    
    // Validate checkout URL format
    if (!data?.url) {
      console.error('\n❌ CRITICAL: Checkout URL is missing from Edge Function response!');
      console.error('  - Expected: data.url to contain Stripe Checkout URL');
      console.error('  - Received: data.url =', data?.url);
      console.error('  - Full response data:', JSON.stringify(data, null, 2));
      console.error('  - This indicates Edge Function succeeded but did not return checkout URL');
      console.error('  - Check Edge Function logs for Stripe API errors');
      console.error('=== EDGE FUNCTION RESPONSE VALIDATION END (MISSING URL) ===');
      throw new Error('Checkout session created but no redirect URL received. Check Edge Function logs.');
    }
    
    if (typeof data?.url !== 'string') {
      console.error('\n❌ CRITICAL: Checkout URL has wrong type!');
      console.error('  - Expected: string');
      console.error('  - Received:', typeof data?.url);
      console.error('  - Value:', data?.url);
      console.error('=== EDGE FUNCTION RESPONSE VALIDATION END (WRONG TYPE) ===');
      throw new Error('Invalid checkout URL format received from server.');
    }
    
    if (!data?.url?.startsWith('https://')) {
      console.error('\n❌ CRITICAL: Checkout URL does not start with https://');
      console.error('  - Received URL:', data?.url);
      console.error('  - This is not a valid Stripe Checkout URL');
      console.error('=== EDGE FUNCTION RESPONSE VALIDATION END (INVALID PROTOCOL) ===');
      throw new Error('Invalid checkout URL protocol. Expected HTTPS URL.');
    }
    
    if (!data?.url?.includes('checkout.stripe.com')) {
      console.warn('\n⚠️ WARNING: Checkout URL does not contain checkout.stripe.com');
      console.warn('  - Received URL:', data?.url);
      console.warn('  - This might be a test/redirect URL, proceeding anyway...');
    }
    
    console.log('\n✅ Checkout URL validation PASSED');
    console.log('  - URL is valid and ready for redirect');
    console.log('  - URL preview:', data?.url?.substring(0, 60) + '...');

    console.log('\n9. Response Metadata:');
    console.log('  - Session ID present:', !!data?.sessionId);
    console.log('  - Session ID preview:', data?.sessionId?.substring(0, 20) + '...');
    console.log('  - Additional fields:', Object.keys(data || {})?.filter(k => k !== 'url' && k !== 'sessionId'));
    
    console.log('\n✅ SUCCESS: Edge Function response validated successfully');
    console.log('=== EDGE FUNCTION RESPONSE VALIDATION END (SUCCESS) ===');
    console.log('=== STRIPE CHECKOUT TROUBLESHOOTING END (SUCCESS) ===');
    
    return { data, error: null };
  },

  // Create payment intent for subscription
  async createSubscriptionPayment(planData, customerInfo) {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-subscription-payment', {
        body: { planData, customerInfo }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create subscription payment error:', error);
      return { data: null, error: error?.message || 'Failed to create payment' };
    }
  },

  // Get subscription plans
  async getSubscriptionPlans() {
    try {
      const { data, error } = await supabase?.from('subscription_plans')?.select('*')?.eq('is_active', true)?.order('price', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get subscription plans error:', error);
      return { data: null, error: error?.message || 'Failed to fetch plans' };
    }
  },

  // Get user's payment methods
  async getPaymentMethods(userId) {
    try {
      // First, sync payment methods from Stripe
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.access_token) {
        await supabase?.functions?.invoke('sync-payment-methods', {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          }
        });
      }

      // Then fetch from local database
      const { data, error } = await supabase?.from('payment_methods')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get payment methods error:', error);
      return { data: null, error: error?.message || 'Failed to fetch payment methods' };
    }
  },

  // Create setup intent for adding payment method
  async createSetupIntent() {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) {
        throw new Error('You must be logged in to add a payment method');
      }

      const { data, error } = await supabase?.functions?.invoke('create-setup-intent', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return { data, error: null };
    } catch (error) {
      console.error('Create setup intent error:', error);
      return { data: null, error: error?.message || 'Failed to initialize payment form' };
    }
  },

  // Attach payment method after SetupIntent confirmation
  async attachPaymentMethod(setupIntentId, setAsDefault = false) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) {
        throw new Error('You must be logged in');
      }

      const { data, error } = await supabase?.functions?.invoke('attach-payment-method', {
        body: { setupIntentId, setAsDefault },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return { data, error: null };
    } catch (error) {
      console.error('Attach payment method error:', error);
      return { data: null, error: error?.message || 'Failed to add payment method' };
    }
  },

  // Remove payment method
  async removePaymentMethod(paymentMethodId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) {
        throw new Error('You must be logged in');
      }

      const { data, error } = await supabase?.functions?.invoke('remove-payment-method', {
        body: { paymentMethodId },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return { data, error: null };
    } catch (error) {
      console.error('Remove payment method error:', error);
      return { data: null, error: error?.message || 'Failed to remove payment method' };
    }
  },

  // Set default payment method
  async setDefaultPaymentMethod(userId, paymentMethodId) {
    try {
      // First, unset all as default
      await supabase?.from('payment_methods')?.update({ is_default: false })?.eq('user_id', userId);

      // Then set the selected one as default
      const { data, error } = await supabase?.from('payment_methods')?.update({ is_default: true })?.eq('id', paymentMethodId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Set default payment method error:', error);
      return { data: null, error: error?.message || 'Failed to set default payment method' };
    }
  },

  // Get user's invoices
  async getInvoices(userId) {
    try {
      // First, sync invoices from Stripe
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.access_token) {
        await supabase?.functions?.invoke('sync-invoices', {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          }
        });
      }

      // Then fetch from local database
      const { data, error } = await supabase?.from('invoices')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get invoices error:', error);
      return { data: null, error: error?.message || 'Failed to fetch invoices' };
    }
  },

  // Change subscription
  async changeSubscription(userId, newPlanId) {
    try {
      const { data, error } = await supabase?.functions?.invoke('change-subscription', {
        body: { userId, newPlanId }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Change subscription error:', error);
      return { data: null, error: error?.message || 'Failed to change subscription' };
    }
  },

  // Cancel subscription
  async cancelSubscription() {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) {
        throw new Error('You must be logged in to cancel subscription');
      }

      const { data, error } = await supabase?.functions?.invoke('cancel-subscription', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return { data, error: null };
    } catch (error) {
      console.error('Cancel subscription error:', error);
      return { data: null, error: error?.message || 'Failed to cancel subscription' };
    }
  },

  // Reactivate subscription
  async reactivateSubscription() {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) {
        throw new Error('You must be logged in to reactivate subscription');
      }

      const { data, error } = await supabase?.functions?.invoke('reactivate-subscription', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return { data, error: null };
    } catch (error) {
      console.error('Reactivate subscription error:', error);
      return { data: null, error: error?.message || 'Failed to reactivate subscription' };
    }
  },

  // Get subscription status
  async getSubscriptionStatus(stripeCustomerId) {
    try {
      const { data, error } = await supabase?.functions?.invoke('get-subscription-status', {
        body: { customer_id: stripeCustomerId }
      })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Get subscription status error:', error)
      return { data: null, error }
    }
  },

  // Format currency
  formatAmount(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    })?.format(amount);
  },

  // Format date
  formatDate(date) {
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  },

  // Check if user is in Pilot mode
  isPilotMode(userProfile) {
    if (!userProfile) return false;
    
    // Pilot mode is when:
    // 1. User has Pro tier
    // 2. Subscription status is 'trialing'
    // 3. Subscription source is 'pilot_coupon'
    return (
      userProfile?.subscription_tier === 'pro' &&
      userProfile?.subscription_status === 'trialing' &&
      userProfile?.subscription_source === 'pilot_coupon'
    );
  },

  // Get Pilot mode expiration date
  getPilotModeExpiration(userProfile) {
    if (!this.isPilotMode(userProfile)) return null;
    return userProfile?.subscription_end_date;
  },

  // Check if pilot mode is expiring soon (within 3 days)
  isPilotExpiringSoon(userProfile) {
    if (!this.isPilotMode(userProfile)) return false;
    
    const expirationDate = new Date(userProfile?.subscription_end_date);
    const now = new Date();
    const threeDaysFromNow = new Date(now.getTime() + (3 * 24 * 60 * 60 * 1000));
    
    return expirationDate <= threeDaysFromNow && expirationDate > now;
  },

  // Check if pilot mode has expired
  isPilotExpired(userProfile) {
    if (!this.isPilotMode(userProfile)) return false;
    
    const expirationDate = new Date(userProfile?.subscription_end_date);
    const now = new Date();
    
    return expirationDate <= now;
  },

  // Get days until pilot expiration
  getDaysUntilPilotExpiration(userProfile) {
    if (!this.isPilotMode(userProfile)) return null;
    
    const expirationDate = new Date(userProfile?.subscription_end_date);
    const now = new Date();
    const diffTime = expirationDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  },

  // Mark pilot warning as shown
  async markPilotWarningShown(userId) {
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({
          pilot_warning_shown: true,
          pilot_warning_shown_at: new Date()?.toISOString()
        })
        ?.eq('id', userId);

      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error('Mark pilot warning shown error:', error);
      return { success: false, error: error?.message };
    }
  },

  // Downgrade expired pilot user to free plan
  async downgradePilotUser(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          subscription_tier: 'free',
          subscription_status: 'inactive',
          subscription_source: 'stripe',
          pilot_warning_shown: false,
          pilot_warning_shown_at: null,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Downgrade pilot user error:', error);
      return { data: null, error: error?.message };
    }
  },
};